import { useMemo, useState } from 'react';
import type { TaskCard, TaskParentChoice } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ComboboxField } from '@/components/ComboboxField';
import { LoadError } from '@/components/LoadError';
import { LoadingState } from '@/components/LoadingState';
import { useLoader } from '@/hooks/use-loader';
import { describeApiError } from '@/lib/api-errors';
import { useCurrentProject, useFormatTaskRef } from '@/lib/current-project';
import { getTaskParents } from '@/lib/tasks-api';
import { parentChoices } from './subtasks';

/**
 * SF-T-132: "Make it a sub-task of…" from a task's ⋯ menu, the way back from "Make it a task of its own":
 * pick a parent from the project's open top-level tasks (GET /api/tasks/parents, SF-T-172: their ids,
 * numbers and titles, not the Board). `onPick` saves it (the caller's optimistic update and toast); the
 * dialog closes once it resolves true.
 */
export function MakeSubtaskDialog({
  open,
  onOpenChange,
  taskId,
  onPick,
  /** The gallery's tasks, instead of loading the project's open ones. */
  tasks: givenTasks,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
  onPick: (parent: TaskParentChoice) => Promise<boolean>;
  tasks?: TaskCard[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" description="Put this task under a larger one, as one of its sub-tasks.">
        <DialogHeader>
          <DialogTitle>Make it a sub-task of…</DialogTitle>
        </DialogHeader>
        {open ? <PickParent taskId={taskId} onPick={onPick} givenTasks={givenTasks} onClose={() => onOpenChange(false)} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function PickParent({
  taskId,
  onPick,
  givenTasks,
  onClose,
}: {
  taskId: string;
  onPick: (parent: TaskParentChoice) => Promise<boolean>;
  givenTasks?: TaskCard[];
  onClose: () => void;
}) {
  const project = useCurrentProject();
  const formatRef = useFormatTaskRef();
  const loaded = useLoader((signal) => getTaskParents(taskId, { signal }), [project?.id, taskId], { enabled: !givenTasks && Boolean(project) });
  const loadedChoices = loaded.data?.data?.tasks;
  // The server's list is already filtered; the gallery's cards go through the same rule here.
  const choices = useMemo(() => (givenTasks ? parentChoices(givenTasks, taskId) : loadedChoices), [givenTasks, loadedChoices, taskId]);
  const [picked, setPicked] = useState<TaskParentChoice | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    const ok = await onPick(picked);
    setSaving(false);
    if (ok) onClose();
  };

  if (!choices) {
    return loaded.data?.error ? (
      <LoadError compact title="Couldn’t load the open tasks" error={describeApiError(loaded.data)} onRetry={loaded.reload} />
    ) : (
      <LoadingState label="Loading the open tasks…" />
    );
  }
  return (
    <>
      <ComboboxField
        id="make-subtask-parent"
        label="Parent task"
        value={picked?.id ?? ''}
        displayValue={picked ? `${formatRef(picked.number)} ${picked.title}` : ''}
        options={choices.map((t) => ({ value: t.id, label: `${formatRef(t.number)} ${t.title}` }))}
        placeholder="Pick a task"
        searchPlaceholder="Search open tasks"
        emptyText="No open task by that name"
        onSelect={(id) => setPicked(choices.find((t) => t.id === id) ?? null)}
      />
      <p className="text-xs text-muted-foreground">
        It leaves the Board and shows in that task’s Sub-tasks, keeping its column, people and comments. Only top-level tasks can have sub-tasks.
      </p>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!picked} disabledReason="Pick the parent task" loading={saving} loadingText="Saving…" onClick={() => void save()}>
          Make it a sub-task
        </Button>
      </DialogFooter>
    </>
  );
}
